import { cn } from '../ui';

/** The product tagline, shown under the wordmark in the Sidebar and AuthGate. */
export const VECTOR_TAGLINE = 'Plan, estimate, ship — intelligence underneath.';

const MARK_SIZES = {
  sm: 'h-7 w-7',
  md: 'h-8 w-8',
  lg: 'h-11 w-11',
} as const;

const WORDMARK_SIZES = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-xl',
} as const;

type LogoSize = keyof typeof MARK_SIZES;

/**
 * The Vector brand mark: an upward, up-and-to-the-right trajectory arrow — a
 * literal "vector" evoking plan → estimate → ship momentum — set in a rounded
 * accent tile. Decorative; the parent carries the accessible name.
 */
export function VectorMark({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-xl bg-accent text-white shadow-card',
        className,
      )}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        className="h-1/2 w-1/2"
        aria-hidden="true"
        focusable="false"
      >
        {/* shaft: bottom-left to top-right */}
        <path
          d="M5 19 19 5"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
        />
        {/* arrowhead at the top-right terminus */}
        <path
          d="M10.5 5H19v8.5"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

export interface LogoProps {
  /** Mark + wordmark scale. Defaults to `md`. */
  size?: LogoSize;
  /** Render the "Vector" wordmark beside the mark. Defaults to `true`. */
  showWordmark?: boolean;
  /** Render the product tagline beneath the wordmark. Defaults to `false`. */
  showTagline?: boolean;
  className?: string;
}

/**
 * Vector lockup: the {@link VectorMark} plus the "Vector" wordmark and, when
 * requested, the product tagline. Used in the Sidebar header and the AuthGate
 * sign-in card.
 */
export function Logo({
  size = 'md',
  showWordmark = true,
  showTagline = false,
  className,
}: LogoProps) {
  return (
    <div className={cn('flex items-center gap-2.5', className)} aria-label="Vector">
      <VectorMark className={MARK_SIZES[size]} />
      {showWordmark && (
        <div className="leading-tight">
          <p
            className={cn(
              'font-semibold tracking-tight text-zinc-50',
              WORDMARK_SIZES[size],
            )}
          >
            Vector
          </p>
          {showTagline && (
            <p className="mt-0.5 text-[11px] leading-snug text-zinc-500">
              {VECTOR_TAGLINE}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default Logo;
